'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Clock, Calendar, Sun, Moon } from 'lucide-react';
import { getHoraCalculatorApi } from '@/lib/api/client';
import { formatLocalizedTime } from '@/lib/i18n/dateFormat';

export interface HoraCalculatorClientProps {
  initialData?: any;
  initialError?: string | null;
}

export function HoraCalculatorClient({
  initialData = null,
  initialError = null
}: HoraCalculatorClientProps) {
  const t = useTranslations('hora');
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState<'day' | 'night'>('day');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [data, setData] = useState<any>(initialData);

  const fetchHora = async (selectedDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHoraCalculatorApi(selectedDate);
      setData(res);
    } catch (err: any) {
      setError(err.message || t('calculationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData) {
      fetchHora(date);
    }
  }, []);

  const formatDisplayTime = (timeStr: string) => {
    if (!timeStr) return '';
    return formatLocalizedTime(timeStr, locale);
  };

  const getPlanetName = (planet: string) => {
    return tGlossary(`planets.${planet}` as any) || planet;
  };

  const getHoraNature = (planet: string) => {
    switch (planet) {
      case 'Sun': return t('natureDynamic');
      case 'Moon': return t('natureAuspicious');
      case 'Mars': return t('natureInauspicious');
      case 'Mercury': return t('natureAuspicious');
      case 'Jupiter': return t('natureHighlyAuspicious');
      case 'Venus': return t('natureAuspicious');
      case 'Saturn': return t('natureSlow');
      default: return t('natureAuspicious');
    }
  };

  const getHoraActivities = (planet: string) => {
    switch (planet) {
      case 'Sun': return t('activitiesSun');
      case 'Moon': return t('activitiesMoon');
      case 'Mars': return t('activitiesMars');
      case 'Mercury': return t('activitiesMercury');
      case 'Jupiter': return t('activitiesJupiter');
      case 'Venus': return t('activitiesVenus');
      case 'Saturn': return t('activitiesSaturn');
      default: return '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="hora-client">
      {/* Header Banner */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 text-center space-y-3 shadow-xs">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--line)] bg-[var(--card-warm)] text-xs font-mono text-[var(--gold)]">
          <Clock className="w-3.5 h-3.5" />
          <span>{t('eyebrow')}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-[var(--foreground)]">
          {t('title')}
        </h1>
        <p className="text-xs sm:text-sm text-[var(--muted)] max-w-xl mx-auto leading-relaxed">
          {t('subtitle')}
        </p>
      </div>

      {/* Date Selector & Tab Switcher */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-full sm:w-1/2 space-y-1">
            <label className="text-xs font-mono text-[var(--muted)] flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[var(--gold)]" />
              <span>{t('selectDate')}</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-[var(--background)] border border-[var(--line)] rounded-xl px-4 py-2.5 text-xs text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            />
          </div>
          <button
            onClick={() => fetchHora(date)}
            disabled={loading}
            className="w-full sm:w-auto mt-auto px-6 py-2.5 rounded-xl bg-[var(--gold)] text-[var(--ink)] font-bold text-xs hover:bg-[var(--gold-soft)] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {t('calculateBtn')}
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 pt-2 border-t border-[var(--line)]">
          <button
            onClick={() => setActiveTab('day')}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
              activeTab === 'day'
                ? 'bg-[var(--gold)] text-[var(--ink)]'
                : 'bg-[var(--background)] text-[var(--muted)] hover:text-[var(--foreground)]'
            }`}
          >
            <Sun className="w-4 h-4" />
            <span>{t('dayTab')}</span>
          </button>
          <button
            onClick={() => setActiveTab('night')}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
              activeTab === 'night'
                ? 'bg-[var(--gold)] text-[var(--ink)]'
                : 'bg-[var(--background)] text-[var(--muted)] hover:text-[var(--foreground)]'
            }`}
          >
            <Moon className="w-4 h-4" />
            <span>{t('nightTab')}</span>
          </button>
        </div>

        {loading && (
          <div className="text-center py-4 text-xs font-mono text-[rgba(220,236,255,0.7)] animate-pulse">
            {t('calculating')}
          </div>
        )}

        {error && (
          <div data-testid="hora-error" className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-mono text-center">
            {error}
          </div>
        )}
      </div>

      {/* Hora Grid Display */}
      {data && (
        <div className="space-y-4" data-testid="hora-result">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {(activeTab === 'day' ? data.day_horas : data.night_horas)?.map((hora: any) => {
              const isGood = hora.quality === 'good';
              const planet = hora.ruler;
              return (
                <div
                  key={hora.hora_number}
                  className={`p-5 rounded-2xl border transition-all space-y-3 ${
                    isGood
                      ? 'bg-[var(--card)] border-emerald-500/30 hover:border-emerald-500/50'
                      : 'bg-[var(--card)] border-[var(--line)] hover:border-[var(--gold)]/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold text-[var(--muted)]">
                      {t('horaLabel', { number: hora.hora_number })}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold uppercase border ${
                      isGood
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                    }`}>
                      {getHoraNature(planet)}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-xl font-serif font-semibold text-[var(--foreground)]">
                      {t('horaHeading', { ruler: getPlanetName(planet) })}
                    </h3>
                    <p className="text-[11px] text-[var(--muted)] leading-tight pt-1">
                      {getHoraActivities(planet)}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-[var(--line)] text-xs font-mono text-[var(--gold)] font-bold">
                    {formatDisplayTime(hora.display)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
